// Offline session management with IndexedDB for secure token storage
// Allows paid users to access the app offline for up to 30 days

const DB_NAME = 'chaoskitchen-offline';
const DB_VERSION = 2; // Bumped version for session store
const SESSION_STORE = 'offlineSession';
const OFFLINE_VALIDITY_DAYS = 30;

export interface OfflineSessionData {
  userId: string;
  email: string;
  tier: 'free' | 'pro' | 'chef_master';
  effectiveTier: 'free' | 'pro' | 'chef_master';
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  trialEndDate: string | null;
  isOnTrial: boolean;
  // Security fields
  createdAt: number; // Timestamp when session was cached
  expiresAt: number; // Timestamp when offline access expires (30 days from creation)
  lastOnlineSync: number; // Last time we synced with server
  // Integrity check - simple hash of critical fields
  checksum: string;
}

// Simple checksum for integrity verification (not cryptographic, just tamper detection)
const generateChecksum = (data: Omit<OfflineSessionData, 'checksum'>): string => {
  const payload = `${data.userId}:${data.tier}:${data.effectiveTier}:${data.createdAt}:${data.expiresAt}`;
  // Simple hash - not cryptographic but prevents casual tampering
  let hash = 0;
  for (let i = 0; i < payload.length; i++) {
    const char = payload.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `ck-${Math.abs(hash).toString(36)}`;
};

const verifyChecksum = (data: OfflineSessionData): boolean => {
  const { checksum, ...rest } = data;
  return checksum === generateChecksum(rest);
};

let dbInstance: IDBDatabase | null = null;

// Timeout for IndexedDB operations
const IDB_TIMEOUT = 10000; // 10 seconds

const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    // Add timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      console.error('[OfflineSession] IndexedDB open timed out');
      reject(new Error('IndexedDB open timed out'));
    }, IDB_TIMEOUT);

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        clearTimeout(timeoutId);
        console.error('[OfflineSession] Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        clearTimeout(timeoutId);
        dbInstance = request.result;
        
        // Handle connection closed unexpectedly
        dbInstance.onclose = () => {
          console.log('[OfflineSession] Database connection closed');
          dbInstance = null;
        };
        
        resolve(dbInstance);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create session store if it doesn't exist
        if (!db.objectStoreNames.contains(SESSION_STORE)) {
          db.createObjectStore(SESSION_STORE, { keyPath: 'id' });
        }

        // Keep existing stores for recipes (synchronized with offlineRecipeCache.ts)
        if (!db.objectStoreNames.contains('savedRecipes')) {
          const savedStore = db.createObjectStore('savedRecipes', { keyPath: 'id' });
          savedStore.createIndex('slug', 'slug', { unique: true });
          savedStore.createIndex('savedAt', 'savedAt', { unique: false });
        }

        if (!db.objectStoreNames.contains('recipePacks')) {
          db.createObjectStore('recipePacks', { keyPath: 'theme' });
        }
      };
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('[OfflineSession] IndexedDB not available:', error);
      reject(error);
    }
  });
};

// LocalStorage pointer for quick access check
const POINTER_KEY = 'ck_offline_session_active';

export const saveOfflineSession = async (
  userId: string,
  email: string,
  tier: 'free' | 'pro' | 'chef_master',
  effectiveTier: 'free' | 'pro' | 'chef_master',
  subscriptionStatus: string | null,
  currentPeriodEnd: string | null,
  trialEndDate: string | null,
  isOnTrial: boolean
): Promise<void> => {
  // Only save session for paid users
  if (effectiveTier === 'free') {
    console.log('[OfflineSession] Free tier - not caching session for offline');
    await clearOfflineSession();
    return;
  }

  try {
    const db = await initDB();
    const now = Date.now();
    const expiresAt = now + (OFFLINE_VALIDITY_DAYS * 24 * 60 * 60 * 1000);

    const sessionData: Omit<OfflineSessionData, 'checksum'> = {
      userId,
      email,
      tier,
      effectiveTier,
      subscriptionStatus,
      currentPeriodEnd,
      trialEndDate,
      isOnTrial,
      createdAt: now,
      expiresAt,
      lastOnlineSync: now,
    };

    const fullSession: OfflineSessionData = {
      ...sessionData,
      checksum: generateChecksum(sessionData),
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SESSION_STORE, 'readwrite');
      const store = transaction.objectStore(SESSION_STORE);

      // Store with a fixed ID so we only have one session
      const request = store.put({ id: 'current', ...fullSession });

      request.onsuccess = () => {
        // Set localStorage pointer for quick check
        localStorage.setItem(POINTER_KEY, '1');
        console.log('[OfflineSession] Session cached for offline use, expires:', new Date(expiresAt).toISOString());
        resolve();
      };
      request.onerror = () => {
        console.error('[OfflineSession] Failed to save session:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[OfflineSession] Error saving session:', error);
    throw error;
  }
};

export const getOfflineSession = async (): Promise<OfflineSessionData | null> => {
  // Quick check - if no pointer, no session
  if (!localStorage.getItem(POINTER_KEY)) {
    return null;
  }

  try {
    const db = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SESSION_STORE, 'readonly');
      const store = transaction.objectStore(SESSION_STORE);

      const request = store.get('current');

      request.onsuccess = () => {
        const data = request.result;
        if (!data) {
          localStorage.removeItem(POINTER_KEY);
          resolve(null);
          return;
        }

        // Remove the id field we used for storage
        const { id, ...sessionData } = data as { id: string } & OfflineSessionData;

        // Verify integrity
        if (!verifyChecksum(sessionData)) {
          console.warn('[OfflineSession] Checksum verification failed - session may be tampered');
          clearOfflineSession();
          resolve(null);
          return;
        }

        // Check expiration
        if (Date.now() > sessionData.expiresAt) {
          console.log('[OfflineSession] Session expired');
          clearOfflineSession();
          resolve(null);
          return;
        }

        resolve(sessionData);
      };
      request.onerror = () => {
        console.error('[OfflineSession] Failed to get session:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[OfflineSession] Error getting session:', error);
    return null;
  }
};

export const clearOfflineSession = async (): Promise<void> => {
  localStorage.removeItem(POINTER_KEY);

  try {
    const db = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SESSION_STORE, 'readwrite');
      const store = transaction.objectStore(SESSION_STORE);

      const request = store.delete('current');

      request.onsuccess = () => {
        console.log('[OfflineSession] Session cleared');
        resolve();
      };
      request.onerror = () => {
        console.error('[OfflineSession] Failed to clear session:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[OfflineSession] Error clearing session:', error);
  }
};

export const updateLastOnlineSync = async (): Promise<void> => {
  const session = await getOfflineSession();
  if (!session) return;

  try {
    const db = await initDB();
    const now = Date.now();

    const updatedSession: Omit<OfflineSessionData, 'checksum'> = {
      ...session,
      lastOnlineSync: now,
      // Extend expiration on online sync
      expiresAt: now + (OFFLINE_VALIDITY_DAYS * 24 * 60 * 60 * 1000),
    };

    const fullSession: OfflineSessionData = {
      ...updatedSession,
      checksum: generateChecksum(updatedSession),
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SESSION_STORE, 'readwrite');
      const store = transaction.objectStore(SESSION_STORE);

      const request = store.put({ id: 'current', ...fullSession });

      request.onsuccess = () => {
        console.log('[OfflineSession] Last sync updated, new expiry:', new Date(fullSession.expiresAt).toISOString());
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[OfflineSession] Error updating sync time:', error);
  }
};

export const isOfflineSessionValid = async (): Promise<boolean> => {
  try {
    const session = await getOfflineSession();
    if (!session) return false;

    // Check if session is still valid
    if (Date.now() > session.expiresAt) {
      await clearOfflineSession();
      return false;
    }

    // Only valid for paid tiers
    if (session.effectiveTier === 'free') {
      await clearOfflineSession();
      return false;
    }

    return true;
  } catch (error) {
    // On any error, return false but don't throw
    console.error('[OfflineSession] Error validating session:', error);
    return false;
  }
};

export const getOfflineSessionDaysRemaining = async (): Promise<number> => {
  const session = await getOfflineSession();
  if (!session) return 0;

  const remaining = session.expiresAt - Date.now();
  return Math.max(0, Math.ceil(remaining / (24 * 60 * 60 * 1000)));
};

export const getOfflineSessionTier = async (): Promise<'free' | 'pro' | 'chef_master'> => {
  const session = await getOfflineSession();
  return session?.effectiveTier ?? 'free';
};
